<script lang="ts">
	import CheckIcon from "@lucide/svelte/icons/check";
	import Section from "#lib/web-ui/primitives/section.svelte";
	import {
		getTemplateContext,
		cloudinaryUrl,
		cloudinarySrcset
	} from "../template-context.svelte";

	const templateContext = getTemplateContext();

	const equityProtectionSteps = [
		{
			label: "Professional Evaluation",
			description:
				"We'll conduct a thorough assessment of your property's current condition and market value to determine the best selling strategy."
		},
		{
			label: "Strategic Marketing Plan",
			description:
				"Our team creates a customized marketing approach designed to attract qualified buyers and generate maximum interest in your property."
		},
		{
			label: "Expert Representation",
			description:
				"We negotiate on your behalf to secure the best possible price and terms, protecting your interests throughout the entire process."
		},
		{
			label: "Smooth Closing Process",
			description:
				"We guide you through closing with professional oversight, ensuring all paperwork is handled correctly and your equity is maximized."
		}
	];

	const equityPerfectForItems = [
		"Properties in fair to good condition with market appeal",
		"Homeowners who want to maximize their sale proceeds",
		"Sellers with flexibility in their moving timeframe",
		"Those seeking professional guidance without upfront costs",
		"Properties that would benefit from targeted marketing"
	];
</script>

<!-- Equity Protection Program Section -->
<Section>
	<!-- Title -->
	<div class="mb-10 w-full">
		<h2 class="text-primary text-lg font-bold">Maximize Your Equity.</h2>
		<h2 class="text-foreground text-2xl font-semibold md:text-4xl md:leading-tight">
			Equity Protection Program
		</h2>
		<p class="text-muted-foreground mt-4 text-pretty">
			Want to maximize your property's value? Our Equity Protection Program offers a
			stress-free path to a higher return while retaining as much equity as possible. With
			professional representation and strategic marketing, we help you achieve the best
			possible outcome without the typical upfront costs or hassles.
		</p>
	</div>
	<!-- End Title -->

	<!-- Grid -->
	<div class="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
		<div class="order-2 h-full md:order-1">
			<img
				class="h-full w-full rounded-xl"
				src={cloudinaryUrl(templateContext.images.needToSell, { width: 768 })}
				srcset={cloudinarySrcset(templateContext.images.needToSell, 640)}
				alt="Equity Protection Program"
				width="640"
				height="427"
				sizes="(max-width: 1024px) 100vw, 50vw"
				loading="eager"
				fetchpriority="high"
			/>
		</div>
		<!-- End Col -->

		<!-- Timeline -->
		<div class="order-1 md:order-2">
			<!-- Heading -->
			<div class="mb-4">
				<h3 class="text-primary text-xs font-medium uppercase">Steps</h3>
			</div>
			<!-- End Heading -->
			{#each equityProtectionSteps as step, i}
				<div class="ms-1 flex gap-x-5">
					<!-- Icon -->
					<div
						class="relative {i < equityProtectionSteps.length - 1
							? 'after:absolute after:start-4 after:top-8 after:bottom-0 after:w-px after:-translate-x-[0.5px] after:bg-gray-200'
							: ''}"
					>
						<div class="relative z-10 flex size-8 items-center justify-center">
							<span
								class="text-primary flex size-8 shrink-0 items-center justify-center rounded-full border border-gray-200 text-xs font-semibold uppercase"
							>
								{i + 1}
							</span>
						</div>
					</div>
					<!-- End Icon -->

					<!-- Right Content -->
					<div class="grow pt-0.5 pb-8">
						<p class="text-muted-foreground lg:text-base">
							<span class="text-foreground font-medium">{step.label}: </span>
							{step.description}
						</p>
					</div>
					<!-- End Right Content -->
				</div>
			{/each}
		</div>
		<!-- End Timeline -->
	</div>
	<!-- End Grid -->

	<!-- Perfect For Section -->
	<div class="mt-10 border-t border-gray-200 pt-10">
		<div class="mb-6">
			<h3 class="text-primary text-xs font-medium uppercase">Perfect For</h3>
			<h4 class="text-foreground mt-2 text-xl font-semibold">
				Who Benefits Most From Our Equity Protection
			</h4>
		</div>

		<div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
			{#each equityPerfectForItems as item}
				<div class="flex items-start gap-3 text-pretty">
					<div class="text-primary mt-1">
						<CheckIcon class="h-5 w-5" />
					</div>
					<div>
						<p class="text-gray-700">{item}</p>
					</div>
				</div>
			{/each}
		</div>
	</div>
	<!-- End Perfect For Section -->
</Section>
